import { Button, Field, Input, Select, TextArea } from '@/components/commons';
import { VideoUploadField } from '@/components/molecules/VideoUploadField';
import { AYUMI_VIDEO_PROFILE } from '@/utils/videoValidation';
import { ImageService, SUPPORTED_FORMATS } from '@/services/image';
import { AYUMI_ANSWER_LABEL_SET_OPTIONS, AyumiAnswerLabelSet } from '@/types/common';
import { NOTIFY_STATUS, notificationMessage } from '@/utils';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Upload } from 'antd';
import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { KNOW_IMAGE_WIDTH, MAX_LENGTH } from '../constants';

/**
 * 「課題を知る」タブ。
 * アプリの縦並び（動画 → タイトル → 本文 → 図表画像 → セルフチェック）と同じ順序で並べる。
 */
interface IProps {
  /** 動画のアップロード中は保存させないため、親に状態を伝える */
  onVideoUploadingChange?: (isUploading: boolean) => void;
}

export const KnowTab: React.FC<IProps> = ({ onVideoUploadingChange }) => {
  const { control, watch, setValue } = useFormContext();
  const [isUploading, setIsUploading] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'questions',
  });

  const knowImageUrl: string | undefined = watch('knowImageUrl');

  const handleUpload = async (file: File) => {
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      notificationMessage({
        message: 'jpg / jpeg / png のみアップロードできます。',
        type: NOTIFY_STATUS.ERROR,
      });

      return Upload.LIST_IGNORE;
    }

    try {
      setIsUploading(true);
      const response = await ImageService.uploadFile(file);
      setValue('knowImageUrl', response?.data?.url, { shouldDirty: true });
    } catch (error) {
      notificationMessage({
        message: '画像のアップロードに失敗しました。',
        type: NOTIFY_STATUS.ERROR,
      });
    } finally {
      setIsUploading(false);
    }

    // antd の自動アップロードは使わず、上で自前送信して URL だけ保持する
    return false;
  };

  return (
    <div className='flex flex-col gap-4'>
      {/* 課題名は topicKey から決まるため入力欄を置かない。ここは一覧カードの説明文 */}
      <Field label='説明文（課題一覧カード）' name='summary'>
        <TextArea
          placeholder='例）判断能力が低下すると、銀行口座や保険の手続きが突然できなくなります。'
          rows={2}
          maxLength={MAX_LENGTH.SUMMARY}
        />
      </Field>

      <VideoUploadField
        name='videoUrl'
        posterName='videoPosterUrl'
        durationName='videoDurationSec'
        widthName='videoWidth'
        heightName='videoHeight'
        profile={AYUMI_VIDEO_PROFILE}
        onUploadingChange={onVideoUploadingChange}
      />

      <Field label='タイトル' name='knowTitle'>
        <Input
          placeholder='例）お金まわりが突然、動かなくなる'
          maxLength={MAX_LENGTH.KNOW_TITLE}
        />
      </Field>

      <Field label='本文' name='knowBody'>
        <TextArea placeholder='本文を入力' rows={4} />
      </Field>

      <div className='flex flex-col gap-2'>
        <span className='text-[13px] text-[#666]'>
          図表画像（幅{KNOW_IMAGE_WIDTH}px）
        </span>
        {knowImageUrl ? (
          <div className='flex flex-col items-start gap-2'>
            <img
              src={knowImageUrl}
              alt='図表'
              className='max-w-full border rounded'
              style={{ width: KNOW_IMAGE_WIDTH }}
            />
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                setValue('knowImageUrl', '', { shouldDirty: true })
              }
            >
              画像を削除
            </Button>
          </div>
        ) : (
          <Upload
            accept='.jpg,.jpeg,.png'
            maxCount={1}
            showUploadList={false}
            beforeUpload={handleUpload}
          >
            <Button icon={<PlusOutlined />} loading={isUploading}>
              画像をアップロード
            </Button>
          </Upload>
        )}
      </div>

      <div className='flex flex-col gap-3 mt-2'>
        <h4 className='font-semibold text-[15px]'>セルフチェック設定</h4>

        {fields.map((field, index) => (
          <div
            key={field.id}
            className='flex flex-col gap-2 p-4 rounded border bg-[#f7f7f7]'
          >
            <div className='flex items-center justify-between'>
              <span className='font-semibold text-[13px]'>
                質問 {index + 1}
              </span>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => remove(index)}
              >
                削除
              </Button>
            </div>

            <Field label='質問文' name={`questions.${index}.question`} required>
              <Input placeholder='質問文を入力' />
            </Field>

            <Field
              label='回答ボタンラベル（3択）'
              name={`questions.${index}.answerLabelSet`}
              required
            >
              <Select
                placeholder='ラベルセットを選択'
                options={AYUMI_ANSWER_LABEL_SET_OPTIONS}
              />
            </Field>

            <Field label='解説文' name={`questions.${index}.explanation`}>
              <TextArea placeholder='解説文を入力' rows={3} />
            </Field>
          </div>
        ))}

        <Button
          icon={<PlusOutlined />}
          onClick={() =>
            append({
              question: '',
              answerLabelSet: AyumiAnswerLabelSet.TALK,
              explanation: '',
            })
          }
        >
          質問を追加
        </Button>
      </div>
    </div>
  );
};
